import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, ListGroupItem, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ConvertedUnits from 'src/components/ConvertedUnits';
import Loader from 'src/components/loader';
import TruncatedTitle from 'src/components/TruncatedTitle';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { MATERIAL_BATCH_ACTIONS_TYPES, MATERIAL_BATCH_ACTIONS_TYPES_MAP } from 'src/utils/constants';
import {
  convertToUserUnits,
  formatThreeDecimalsNumber,
  performReverseConversion,
} from 'src/utils/conversions';
import routes from 'src/utils/routes';
import { getUuid } from 'src/utils/url';
import { getRouteURI } from 'src/utils/url';

import ModuleTypeDot from './module-type-dot';

const TransactionsList = ({
  transactions,
  containers,
  units,
  amountLeftToReclaim,
  updateReconciledTransactions,
  editResetKey,
  isEditing = false,
  batchActionsProps,
  emptyMessage = 'No transactions',
}) => {
  const [initialConvertedValues, setInitialConvertedValues] = useState({});
  const [reconciledValues, setReconciledValues] = useState({});
  const { closeAllActionPanels } = useActionPanelStore();

  const {
    batchActions,
    isLoadingBatchActions,
    batchActionsError,
  } = batchActionsProps;

  // Reset input values when editResetKey changes
  useEffect(() => {
    const initialValues = {};
    transactions.forEach(transaction => {
      const { quantity } = convertToUserUnits(transaction.quantity, units);
      initialValues[transaction.uri] = quantity;
    });
    setReconciledValues(initialValues);
    setInitialConvertedValues(initialValues);
  }, [editResetKey, transactions, units]);


  if (_isEmpty(transactions)) {
    return <ListGroupItem>{emptyMessage}</ListGroupItem>;
  }

  const renderBatchActions = (batchAction, isReclaimedAction) => {
    if (isLoadingBatchActions) {
      return <Loader inline className='spacer-left' showText={false} />
    }

    if (!batchAction || batchActionsError) return null;

    if (batchAction) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip">{batchAction}</Tooltip>}
        >
          <div
            className="module-action-type"
            style={{ backgroundColor: isReclaimedAction ? 'steelblue' : 'sandybrown' }}
          >
            {batchAction.slice(0, 1)}
          </div>
        </OverlayTrigger>
      )
    }

  }

  const handleInputChange = (uri, value, convertedUnits) => {
    // Prevents very large numbers from being added
    if (value.length > 10) return;

    setReconciledValues(prev => ({
      ...prev,
      [uri]: value,
    }));

    // Only update reconciledTransactions if value differs from initial
    if (value === String(initialConvertedValues[uri])) {
      updateReconciledTransactions(prev => {
        const { [uri]: _, ...rest } = prev;
        return rest;
      });
    } else {
      let updateValue = value;
      if (convertedUnits !== units) {
        const unconvertedValue = performReverseConversion(value, convertedUnits, units);
        updateValue = unconvertedValue.quantity;
      }

      updateReconciledTransactions(prev => ({
        ...prev,
        [uri]: updateValue,
      }));
    }
  };

  return (
    transactions.map(transaction => {
      const container = containers[transaction.container];
      const containerName = container?.name || 'Unknown';
      const containerUri = container?.uri;

      const {
        quantity: convertedQuantity,
        units: convertedUnits,
      } = convertToUserUnits(transaction.quantity, units);

      const inputValue = reconciledValues[transaction.uri] ?? convertedQuantity;
      const batchActionUri = transaction.material_batch_action;
      const batchActionType = batchActions?.[batchActionUri]?.metadata;

      const batchAction = MATERIAL_BATCH_ACTIONS_TYPES_MAP[batchActionType?.unload_type] || 'N/A';
      const isReclaimedAction = batchActionType?.unload_type === MATERIAL_BATCH_ACTIONS_TYPES.reclaimed;

      const initialAvailableQuantity = transactions[0].initial_quantity_available;
      const quantityPercentage = Math.round((transaction.quantity / initialAvailableQuantity) * 100);

      return (
        <ListGroupItem key={transaction.uri} className="py-2">
          <Row className="align-items-center transaction-table-cell">
            <Col xs={7} className="d-flex align-items-center">
              <ModuleTypeDot containerType={transaction.container_type} />
              <Link
                className='transaction-link-btn'
                to={getRouteURI(routes.permanentContainerDetails, {
                  uuid: getUuid(containerUri),
                })}
                onClick={closeAllActionPanels}
              >
                <TruncatedTitle
                  title={containerName}
                  titleClassName='mb-0 fw-medium fs-14'
                  maxTextLength={28}
                  maxTextLengthMobile={19} />
              </Link>
              {renderBatchActions(batchAction, isReclaimedAction)}
            </Col>
            <Col xs={5}>
              {isEditing ? (
                <div className="input-group flex-nowrap" style={{ minHeight: '38px' }}>
                  <input
                    name="reconciledAmount"
                    min="0"
                    max={amountLeftToReclaim + Number(inputValue || 0)}
                    maxLength="10"
                    value={inputValue}
                    className="form-control w-100"
                    type="number"
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = /^\d*\.?\d{0,3}$/;

                      if (value === '' || regex.test(value)) {
                        handleInputChange(transaction.uri, value, convertedUnits);
                      }
                    }}
                  />
                  <span
                    className="input-group-text"
                    style={{ borderTopLeftRadius : 0, borderBottomLeftRadius: 0, borderLeft: 0 }}
                  >
                    { convertedUnits }
                  </span>
                </div>
              ) : (
                <>
                  <ConvertedUnits
                    displayZero
                    quantity={formatThreeDecimalsNumber(transaction.quantity)}
                    units={units} />&nbsp;
                  <span className="text-muted">
                    {quantityPercentage > 0 && `(${quantityPercentage}%)`}
                  </span>
                </>
              )}
            </Col>
          </Row>
        </ListGroupItem>
      );
    })
  );
};

TransactionsList.propTypes = {
  /* eslint-disable camelcase */
  transactions: PropTypes.arrayOf(PropTypes.shape({
    uri: PropTypes.string.isRequired,
    container: PropTypes.string.isRequired,
    container_type: PropTypes.string.isRequired,
    quantity: PropTypes.number,
    initial_quantity_available: PropTypes.number,
  })).isRequired,
  containers: PropTypes.objectOf(PropTypes.shape({
    name: PropTypes.string,
  })).isRequired,
  batchActionsProps: PropTypes.shape({
    batchActions: PropTypes.shape({
      metadata: PropTypes.string,
    }),
    isLoadingBatchActions: PropTypes.bool,
    batchActionsError: PropTypes.bool,
  }).isRequired,
  units: PropTypes.string,
  emptyMessage: PropTypes.string,
  amountLeftToReclaim: PropTypes.number,
  updateReconciledTransactions: PropTypes.func,
  isEditing: PropTypes.bool,
  editResetKey: PropTypes.number,
};

export default TransactionsList;
